// ユーザー棚卸し: SmartHR・カオナビの従業員一覧CSVをアップロードし、メール突合で
// 「名簿に無い SF アクティブユーザー = 退職者アカウント残存の疑い」を検出する(モック)。
// CSV はブラウザ内で処理しサーバへ送信しない。無効化の書き戻しは行わない(後工程, §3.2)。
import { SF_USERS, SAMPLE_ROSTER_CSV, type SfUser } from "./audit-data";
import { reconcileUsers, rosterFromCsv, type RosterRow, type ReconcileResult } from "./user-reconcile-logic";
import { createFileUpload } from "../ui/file-upload";
import { createFilterableTable, type FtColumn } from "../ui/filterable-table";
import { createDataTable, type Column } from "../ui/data-table";
import { createLoadingOverlay } from "../ui/loading-overlay";
import { downloadCsv, escapeCsvField } from "../ui/csv";
import { showToast } from "../ui/toast";

function residualToCsv(users: readonly SfUser[]): string {
  const rows = users.map((u) => [u.email, u.name, u.profile, u.lastLogin].map(escapeCsvField).join(","));
  return ["email,name,profile,lastLogin", ...rows].join("\n") + "\n";
}

function summaryCard(label: string, value: string, emphasize: boolean): HTMLElement {
  const card = document.createElement("article");
  card.className = "rounded-lg border border-slate-200 p-4";
  const h = document.createElement("h3");
  h.className = "text-sm font-medium text-slate-600";
  h.textContent = label;
  const v = document.createElement("p");
  v.className = "mt-1 text-2xl font-semibold " + (emphasize ? "text-red-700" : "text-slate-900");
  v.textContent = value;
  card.append(h, v);
  return card;
}

export interface UserReconcileDeps {
  // 接続時に渡す実 SF アクティブユーザー。省略時はサンプル（SF_USERS）で突合する。
  // いずれの場合も従業員名簿 CSV はブラウザ内で処理し、サーバへは送信しない。
  readonly sfUsers?: readonly SfUser[];
}

export function renderUserReconcile(root: HTMLElement, deps?: UserReconcileDeps): void {
  root.replaceChildren();

  const usingRealSfUsers = deps?.sfUsers !== undefined;
  const sfUsers = deps?.sfUsers ?? SF_USERS;

  const intro = document.createElement("p");
  intro.className = "text-sm text-slate-600";
  intro.textContent =
    "SmartHR・カオナビから取得した従業員一覧CSVをアップロードすると、メールアドレスの突合で退職者アカウントの残存（名簿に無い Salesforce アクティブユーザー）を検出します。CSV はブラウザ内で処理され、サーバへは送信されません。";

  const alert = document.createElement("div");
  alert.setAttribute("role", "alert");
  alert.setAttribute("aria-live", "assertive");
  alert.className = "min-h-[1.25rem] text-sm text-red-700";

  const uploadHost = document.createElement("div");
  uploadHost.className = "mt-4 max-w-xl";
  uploadHost.append(
    createFileUpload({ accept: ".csv", label: "従業員一覧CSV（SmartHR・カオナビ）", onText: run }),
  );

  const sampleBtn = document.createElement("button");
  sampleBtn.type = "button";
  sampleBtn.className =
    "mt-2 rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100";
  sampleBtn.textContent = "サンプルCSVで試す";
  sampleBtn.addEventListener("click", () => run(SAMPLE_ROSTER_CSV, "sample-roster.csv"));

  const resultHost = document.createElement("div");
  resultHost.className = "mt-6";

  const overlay = createLoadingOverlay({ message: "CSV を解析しています" });

  function run(text: string, fileName: string): void {
    alert.textContent = "";
    overlay.show();
    queueMicrotask(() => {
      const { rows, error } = rosterFromCsv(text);
      overlay.hide();
      if (error !== undefined) {
        alert.textContent = error;
        resultHost.replaceChildren();
        return;
      }
      const result = reconcileUsers(rows, sfUsers);
      renderResult(fileName, rows.length, result);
      showToast({
        tone: result.residual.length > 0 ? "warning" : "success",
        title: "棚卸しが完了しました",
        message: "残存疑い " + result.residual.length + " 件 / 突合 " + result.matchedCount + " 件",
      });
    });
  }

  function renderResult(fileName: string, rosterCount: number, result: ReconcileResult): void {
    resultHost.replaceChildren();

    const meta = document.createElement("p");
    meta.className = "text-sm text-slate-500";
    meta.textContent =
      fileName +
      "（名簿 " +
      rosterCount +
      " 名）と Salesforce ユーザー" +
      (usingRealSfUsers ? "（実データ）" : "（サンプル）") +
      "を突合しました。";

    const grid = document.createElement("div");
    grid.className = "mt-3 grid gap-4 sm:grid-cols-3";
    grid.append(
      summaryCard("突合（名簿と一致）", result.matchedCount + " 名", false),
      summaryCard("退職者アカウント残存の疑い", "残存疑い " + result.residual.length + " 件", result.residual.length > 0),
      summaryCard("名簿のみ（SF 未作成・参考）", result.notInSf.length + " 名", false),
    );

    const residualSection = document.createElement("section");
    residualSection.className = "mt-6";
    const rh = document.createElement("h2");
    rh.className = "text-lg font-medium text-slate-900";
    rh.textContent = "退職者アカウント残存の疑い";
    const csvBtn = document.createElement("button");
    csvBtn.type = "button";
    csvBtn.className =
      "mt-2 rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100";
    csvBtn.textContent = "残存疑いを CSV 出力";
    csvBtn.addEventListener("click", () => downloadCsv("residual-accounts.csv", residualToCsv(result.residual)));

    const residualColumns: FtColumn<SfUser>[] = [
      { key: "email", header: "メールアドレス", cell: (u) => u.email, sortable: true, sortAccessor: (u) => u.email },
      { key: "name", header: "氏名", cell: (u) => u.name },
      { key: "profile", header: "プロファイル", cell: (u) => u.profile },
      { key: "lastLogin", header: "最終ログイン", cell: (u) => u.lastLogin, sortable: true, sortAccessor: (u) => u.lastLogin },
    ];
    const residualTable = createFilterableTable<SfUser>({
      caption: "退職者アカウント残存の疑い一覧",
      columns: residualColumns,
      rows: result.residual,
      searchable: true,
      searchAccessor: (u) => u.email + " " + u.name,
      emptyMessage: "残存疑いはありません。",
      rowTestId: "residual-row",
    });
    const note = document.createElement("p");
    note.className = "mt-2 text-sm text-slate-500";
    note.textContent =
      "アカウントの無効化は Salesforce 側での対応が必要です（本画面からの書き戻しは行いません・サンプル）。";
    residualSection.append(rh, csvBtn, residualTable, note);

    const notInSfSection = document.createElement("section");
    notInSfSection.className = "mt-6";
    const nh = document.createElement("h2");
    nh.className = "text-lg font-medium text-slate-900";
    nh.textContent = "名簿のみ（SF 未作成・参考）";
    const notInSfColumns: Column<RosterRow>[] = [
      { key: "email", header: "メールアドレス", cell: (r) => r.email },
      { key: "name", header: "氏名", cell: (r) => r.name },
      { key: "status", header: "在籍状況", cell: (r) => r.status },
    ];
    notInSfSection.append(
      nh,
      createDataTable<RosterRow>({
        caption: "名簿に居るが Salesforce に存在しないユーザー",
        columns: notInSfColumns,
        rows: result.notInSf,
        emptyMessage: "該当はありません。",
        rowTestId: "not-in-sf-row",
      }),
    );

    resultHost.append(meta, grid, residualSection, notInSfSection);
  }

  root.append(intro, alert, uploadHost, sampleBtn, resultHost, overlay.element);
}
